import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {
    deleteSessionStorage,
    getSessionStorage,
    scrollToTop,
    setSessionStorage
} from "../../../../../CommonFunctions";
import {ICustomerPortalService, OnlineMotorClaimRequest, Policy, Types, FalconNavigatorHeader, POLICY_STATUS_MTC} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import * as moment from "moment";
import {BackButton} from "../../../../../components/navigation/BackButton";
import {NextButton} from "./NextButton";
import {IUrgentClaim} from "./UrgentClaimPage";
import {IWindScreenOrGlass} from "./WindscreenOrGlassPage";
import {TextAreaComponent} from "../../../../../components/inputs/TextAreaComponent";
import {IPolicyDetails} from "./CoverDetailsPage";
import {ClaimDatePicker} from "../../../../../components/claims/ClaimDatePicker";
import {AccidentTimeInput} from "../../../../../components/claims/AccidentTimeInput";
import {AllianzOnlineBadge} from "../../../../../components/AllianzOnlineBadge";

interface IWhenDidItHappenPageState {
    onlineMotorClaimRequest: OnlineMotorClaimRequest;
    whenDidItHappen: IWhenDidItHappen;
    urgentClaim: IUrgentClaim;
    windscreenOrGlass: IWindScreenOrGlass;
    lossDateValid?: boolean;
    accidentTimeMessage: string;
}

export interface IWhenDidItHappen {
    isAccidentHourValid: boolean;
    isClaimReasonDescriptionValid: boolean;
    policy?: Policy;
}

@authorize
export class WhenDidItHappenPagePlain extends React.Component<IWithRouterProps, IWhenDidItHappenPageState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IWithRouterProps) {
        super(props);
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = getSessionStorage("onlineMotorClaimRequest");
        const whenDidItHappen: IWhenDidItHappen = getSessionStorage("whenDidItHappen");
        const urgentClaim: IUrgentClaim = getSessionStorage("urgentClaim");
        const windscreenOrGlass: IWindScreenOrGlass = getSessionStorage("windscreenOrGlass");
        const policyDetails: IPolicyDetails = getSessionStorage("policyDetails");

        this.state = {
            onlineMotorClaimRequest: onlineMotorClaimRequest !== null ? onlineMotorClaimRequest : new OnlineMotorClaimRequest(),
            whenDidItHappen: whenDidItHappen !== null ? whenDidItHappen : {
                policy: policyDetails.selectedPolicy,
                isClaimReasonDescriptionValid: true
            } as IWhenDidItHappen,
            urgentClaim: urgentClaim !== null ? urgentClaim : {
                isUrgentClaim: null,
                showUrgentClaimPopup: false
            } as IUrgentClaim,
            windscreenOrGlass: windscreenOrGlass !== null ? windscreenOrGlass : {
                windscreenOrGlassDamaged: null,
                showWindscreenPopup: false
            } as IWindScreenOrGlass,
            accidentTimeMessage: "Please answer this to carry on"
        }

        this.setLossDateValid = this.setLossDateValid.bind(this);
    };

    public componentDidMount(): void {
        if (!this.isUserInformationPresent()){
            this.props.navigate("/dashboard/windscreen/urgent/coverDetails", {state: {showOverlay: true}});
        }

        scrollToTop();
        this.pluginTemplates.setPageTitle("Make a car claim");

    }

    public render() {
        const {onlineMotorClaimRequest, whenDidItHappen} = this.state;

        if (whenDidItHappen.policy) {
            const policy: Policy = whenDidItHappen.policy;
            const endDate: moment.Moment = moment.utc(policy.endDate, moment.ISO_8601);
            const maxDate: string | null = (policy.status === POLICY_STATUS_MTC && endDate.isBefore(moment())) ? policy.endDate : null;

            return (
                <React.Fragment>
                    <FalconHeader/>

                    <div className="page-contents">
                        <AllianzOnlineBadge/>
                        <FalconNavigatorHeader
                            back="/dashboard/windscreen/urgent/coverDetails"
                            title="When and where?"
                        />
                        <div className="section-panel pb-2">
                            <ClaimDatePicker onDateSelect={this.handleAccidentDate}
                                date={onlineMotorClaimRequest.accidentDate}
                                minDate={whenDidItHappen.policy.startDate}
                                mtcMaxDate={maxDate}
                            />
                            {Boolean(onlineMotorClaimRequest.accidentDate) &&
                                <AccidentTimeInput onlineClaimRequest={onlineMotorClaimRequest}
                                    policy={policy}
                                    handleAccidentHour={this.handleAccidentHour}
                                    handleRadioSelection={this.handlePMorAMCheckBoxes}
                                    setInputValidity={this.setLossDateValid}
                                    futureDateValidationMessage={"You cannot make claim in the future, if you are unsure please call us on 0800 030 4898"}
                                    pastDateValidationMessage={"You cannot make claim before your cover started, if you are unsure please call us on 0800 030 4898"}
                                    inputValid={this.state.whenDidItHappen.isAccidentHourValid}
                                    validationMessage={this.state.accidentTimeMessage}
                                />
                            }
                        </div>
                        <h1 className="page-heading-black">What happened?</h1>
                        <div className="section-panel">
                            <TextAreaComponent
                               questionLabel={"Give us a short description of what happened. Include the cause of your claim"}
                               isValid={this.state.whenDidItHappen.isClaimReasonDescriptionValid}
                               onChange={this.handleClaimReasonDescription}
                               id={"shortDescriptionOfClaim"}
                               maxLength={300}
                               value={this.state.onlineMotorClaimRequest.claimingReasonDescription}
                               largeTextArea={false}
                            />
                        </div>
                        <NextButton buttonId={"submitWhenDidItHappenPage"}
                                    disabled={!this.isFormValid()}
                                    onClick={this.handleSubmit}/>
                        <BackButton isTop={false}/>
                    </div>

                    <FalconFooter/>
                </React.Fragment>
            )
        } else {
            return (<Overlay show={true}/>)
        }
    }

    private isFormValid = (): boolean => {
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
        const urgentClaim: IUrgentClaim = {...this.state.urgentClaim};
        const windscreenOrGlass: IWindScreenOrGlass = {...this.state.windscreenOrGlass};

        const isTimeValid: boolean = onlineMotorClaimRequest.accidentDate !== undefined &&
            (onlineMotorClaimRequest.accidentHour !== undefined && onlineMotorClaimRequest.accidentHour !== "") &&
            (onlineMotorClaimRequest.accidentHourTime !== undefined && onlineMotorClaimRequest.accidentHourTime !== "");

        const isOtherInformationValid: boolean = onlineMotorClaimRequest.claimingReasonDescription !== undefined &&
            onlineMotorClaimRequest.claimingReasonDescription !== "" && urgentClaim.isUrgentClaim !== undefined &&
            windscreenOrGlass.windscreenOrGlassDamaged !== undefined;

        return isTimeValid && isOtherInformationValid && this.isUserInformationPresent();
    };

    private isUserInformationPresent = (): boolean => {
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};

        return !(!onlineMotorClaimRequest.dateOfBirth ||
            !onlineMotorClaimRequest.contactEmail ||
            !onlineMotorClaimRequest.firstName ||
            !onlineMotorClaimRequest.lastName ||
            !onlineMotorClaimRequest.fullAddress ||
            !onlineMotorClaimRequest.contactMobile);
    };

    private handleSubmit = (event: any): void => {
        if (event.target.disabled) {
            event.preventDefault();
        } else {
            const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
            onlineMotorClaimRequest.claimSubmissionDate = moment().format("YYYY-MM-DD HH:mm");
            this.setState({onlineMotorClaimRequest}, () => {
                // Change for pushing to ClaimsCenter temporarily reverted, when change need to be put back refer to commit b7811ded1d6613af426ed50dcc745f3b90f37d1c
                this.customerPortalService.createOnlineMotorClaimRequest(this.state.onlineMotorClaimRequest).subscribe();
                this.clearSessionStorage();
                this.props.navigate("/dashboard/windscreen/urgent/coverDetails/occur/success", {state: {showOverlay: true}});
            });
        }
    };

    private clearSessionStorage = (): void => {
        deleteSessionStorage("urgentClaim");
        deleteSessionStorage("whenDidItHappen");
        deleteSessionStorage("windscreenOrGlass");
        deleteSessionStorage("policyDetails");
        deleteSessionStorage("contactDetails");
    };

    private handleClaimReasonDescription = (event: any): void => {
        const state: string = event.target.value;
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
        const whenDidItHappen: IWhenDidItHappen = {...this.state.whenDidItHappen!};
        onlineMotorClaimRequest.claimingReasonDescription = state;
        whenDidItHappen.isClaimReasonDescriptionValid = state !== "";
        this.setState({onlineMotorClaimRequest, whenDidItHappen});
        setSessionStorage("onlineMotorClaimRequest", onlineMotorClaimRequest);
        setSessionStorage("whenDidItHappen", whenDidItHappen);
    };

    private handleAccidentDate = (state: string): void => {
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
        onlineMotorClaimRequest.accidentDate = state;
        onlineMotorClaimRequest.accidentHourTime = "";
        onlineMotorClaimRequest.accidentHour = "";
        this.setState({onlineMotorClaimRequest});
        setSessionStorage("onlineMotorClaimRequest", onlineMotorClaimRequest);
    };

    private handleAccidentHour = (event: any): void => {
        event.preventDefault();
        const state: string = event.target.value;
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
        const whenDidItHappen: IWhenDidItHappen = {...this.state.whenDidItHappen!};
        onlineMotorClaimRequest.accidentHour = state;
        onlineMotorClaimRequest.accidentHourTime = "";
        whenDidItHappen.isAccidentHourValid = state !== "";
        this.setState({onlineMotorClaimRequest, whenDidItHappen});
        setSessionStorage("onlineMotorClaimRequest", onlineMotorClaimRequest);
        setSessionStorage("whenDidItHappen", whenDidItHappen);
    };

    private handlePMorAMCheckBoxes = (event: any): void => {
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = this.state.onlineMotorClaimRequest;
        onlineMotorClaimRequest.accidentHourTime = event.target.value;

        this.setState({
            onlineMotorClaimRequest
        });

        setSessionStorage("onlineMotorClaimRequest", onlineMotorClaimRequest);
    };

    private setLossDateValid(lossDateValid: boolean, validationMessage: string): void {
        this.setState({
            lossDateValid,
            accidentTimeMessage: validationMessage
        })
    }
}

export const WhenDidItHappenPage = withRouter(WhenDidItHappenPagePlain)
